import { FluxoAprovacaoPedidoStatus } from '../@types/enums/fluxo-aprovacao-pedido-status-enum';
import { EHttpStatusCode } from '../@types/enums/http-status-code-enums';
import { FluxoAprovacaoPedidoEntity } from '../@types/entity/fluxo-aprovacao-pedido-entity';
import { PedidoDetalhe } from '../@types/entity/pedido-detalhe-entity';
import { ServiceOperationResult } from 'service/api/rest/rest-usecase';
import { SolicitacaoItem } from 'service/api/rest/fluxo-aprovacao-pedido-usecase';
import { getEnv } from './window-helper';
import { Filial } from 'service/api/rest/filial-usecase';

/** View-model da tela do operador: concentra os flags de render fora do JSX. */
export interface OperadorView {
  podeEnviar: boolean;
  geracaoLiberada: boolean;
  mostrarGerar: boolean;
  mostrarBloqueio: boolean;
  pedidoGerado: boolean;
  reprovado: boolean;
}

/**
 * Ações disponíveis na visualização do pedido do novo fluxo, derivadas do
 * status + se o usuário pode aprovar (vínculo aprovador na loja do pedido). Mantém a lógica de
 * decisão fora do JSX.
 */
export interface AcoesPedidoView {
  mostrarEnviar: boolean;
  mostrarAprovar: boolean;
  mostrarReprovar: boolean;
  mostrarValidar: boolean;
  mostrarEditar: boolean;
  mostrarGerar: boolean;
  geracaoLiberada: boolean;
  mostrarBloqueio: boolean;
  /** Há solicitação PENDENTE que impede aprovar/validar/gerar no turno atual. */
  bloqueadoPorPendencia: boolean;
  /** Editar só é permitido após aceitar/recusar todas as solicitações pendentes que o usuário pode decidir. */
  editarBloqueadoPorPendencia: boolean;
  /** Enviar para aprovação fica bloqueado enquanto houver solicitação pendente. */
  enviarBloqueadoPorPendencia: boolean;
  terminal: boolean;
}

/** Rotulos de exibicao por status (secao 7 da especificacao). */
export const FLUXO_APROVACAO_PEDIDO_STATUS_LABEL: Record<FluxoAprovacaoPedidoStatus, string> = {
  [FluxoAprovacaoPedidoStatus.EM_ELABORACAO]: 'Em elaboração',
  [FluxoAprovacaoPedidoStatus.AGUARDANDO_APROVACAO_GESTOR]: 'Aguardando aprovação do Gestor',
  [FluxoAprovacaoPedidoStatus.ALTERADO_PELO_GESTOR]: 'Alterado pelo Gestor',
  [FluxoAprovacaoPedidoStatus.AGUARDANDO_VALIDACAO_OPERADOR]: 'Aguardando aprovação do Operador',
  [FluxoAprovacaoPedidoStatus.APROVADO_PELO_GESTOR]: 'Aprovado pelo Gestor',
  [FluxoAprovacaoPedidoStatus.APROVADO_PELO_OPERADOR]: 'Aprovado pelo Operador',
  [FluxoAprovacaoPedidoStatus.APROVADO_PARA_GERACAO]: 'Aprovado para geração',
  [FluxoAprovacaoPedidoStatus.PEDIDO_GERADO]: 'Pedido gerado',
  [FluxoAprovacaoPedidoStatus.AGUARDANDO_AJUSTE]: 'Aguardando ajuste',
  [FluxoAprovacaoPedidoStatus.REPROVADO]: 'Reprovado',
};

export interface StatusBadgeStyle {
  bg: string;
  text: 'white' | 'dark';
}

export const FLUXO_APROVACAO_PEDIDO_STATUS_BADGE: Record<FluxoAprovacaoPedidoStatus, StatusBadgeStyle> = {
  [FluxoAprovacaoPedidoStatus.EM_ELABORACAO]: { bg: 'secondary', text: 'white' },
  [FluxoAprovacaoPedidoStatus.AGUARDANDO_APROVACAO_GESTOR]: { bg: 'warning', text: 'dark' },
  [FluxoAprovacaoPedidoStatus.APROVADO_PELO_GESTOR]: { bg: 'warning', text: 'dark' },
  [FluxoAprovacaoPedidoStatus.AGUARDANDO_VALIDACAO_OPERADOR]: { bg: 'info', text: 'dark' },
  [FluxoAprovacaoPedidoStatus.APROVADO_PELO_OPERADOR]: { bg: 'info', text: 'dark' },
  [FluxoAprovacaoPedidoStatus.ALTERADO_PELO_GESTOR]: { bg: 'primary', text: 'white' },
  [FluxoAprovacaoPedidoStatus.APROVADO_PARA_GERACAO]: { bg: 'success', text: 'white' },
  [FluxoAprovacaoPedidoStatus.PEDIDO_GERADO]: { bg: 'success', text: 'white' },
  [FluxoAprovacaoPedidoStatus.AGUARDANDO_AJUSTE]: { bg: 'warning', text: 'dark' },
  [FluxoAprovacaoPedidoStatus.REPROVADO]: { bg: 'danger', text: 'white' },
};

const STATUS_BADGE_PADRAO: StatusBadgeStyle = { bg: 'secondary', text: 'white' };

export function getStatusBadge(status: FluxoAprovacaoPedidoStatus): StatusBadgeStyle {
  return FLUXO_APROVACAO_PEDIDO_STATUS_BADGE[status] ?? STATUS_BADGE_PADRAO;
}

/** Mensagem exibida ao operador quando a geracao esta bloqueada (secao 11). */
export const MENSAGEM_BLOQUEIO_GERACAO =
  'A geração do pedido só é liberada após a aprovação conjunta do Operador e do Gestor.';

/** Mensagem de impedimento quando há solicitações pendentes a decidir. */
export const MENSAGEM_ENVIAR_PENDENCIA_CONFIGURACAO =
  'Não é possível enviar: falta um aprovador ativo vinculado à loja do pedido (pendência de configuração).';

export const MENSAGEM_EDITAR_PENDENCIA =
  'Aceite ou recuse todas as solicitações de edição pendentes antes de editar o pedido.';

export const MENSAGEM_BLOQUEIO_PENDENCIA =
  'Existem solicitações de alteração pendentes. Decida (aceitar/recusar) todas antes de aprovar o pedido.';

/** Mensagem padrao amigavel quando a API bloqueia uma acao (HTTP 409). */
export const MENSAGEM_BLOQUEIO_PADRAO =
  'Ação bloqueada: o pedido não está em um estado que permita esta operação.';

/** Exibido quando o CSV ainda não pode ser baixado (pedido não aprovado). */
export const MENSAGEM_CSV_BLOQUEADO = 'O download do CSV só é liberado após a aprovação do pedido.';

/**
 * Exibido quando o pedido aprovado não tem nenhum item. Como o CSV não leva cabeçalho, o
 * arquivo sairia com 0 byte: melhor avisar do que entregar um download vazio silencioso.
 */
export const MENSAGEM_CSV_SEM_ITENS = 'Este pedido não possui itens para exportar.';

const MENSAGEM_API_GENERICA = 'Ops!!! Algo deu errado';

/** Status intermediarios do fluxo em que a geracao ainda esta bloqueada. */
const STATUS_GERACAO_BLOQUEADA: FluxoAprovacaoPedidoStatus[] = [
  FluxoAprovacaoPedidoStatus.EM_ELABORACAO,
  FluxoAprovacaoPedidoStatus.AGUARDANDO_APROVACAO_GESTOR,
  FluxoAprovacaoPedidoStatus.ALTERADO_PELO_GESTOR,
  FluxoAprovacaoPedidoStatus.AGUARDANDO_VALIDACAO_OPERADOR,
  FluxoAprovacaoPedidoStatus.APROVADO_PELO_GESTOR,
  FluxoAprovacaoPedidoStatus.APROVADO_PELO_OPERADOR,
  FluxoAprovacaoPedidoStatus.AGUARDANDO_AJUSTE,
];

export function getStatusLabel(status: FluxoAprovacaoPedidoStatus): string {
  return FLUXO_APROVACAO_PEDIDO_STATUS_LABEL[status] ?? String(status);
}

/** RF01: operador so pode enviar para aprovacao a partir de "Em elaboração". */
export function podeEnviarParaAprovacao(status: FluxoAprovacaoPedidoStatus): boolean {
  return status === FluxoAprovacaoPedidoStatus.EM_ELABORACAO
    || status === FluxoAprovacaoPedidoStatus.AGUARDANDO_AJUSTE;
}

/** Geracao definitiva liberada apenas apos aprovacao conjunta. */
export function podeGerarPedido(status: FluxoAprovacaoPedidoStatus): boolean {
  return status === FluxoAprovacaoPedidoStatus.APROVADO_PARA_GERACAO;
}

/** Geracao bloqueada enquanto o pedido esta em fluxo de aprovacao (secao 11). */
export function geracaoBloqueada(status: FluxoAprovacaoPedidoStatus): boolean {
  return STATUS_GERACAO_BLOQUEADA.includes(status);
}

/**
 * Status em que o pedido conta como APROVADO: a aprovação conjunta (Operador + Gestor) já foi
 * concluída. `Aprovado_Para_Geracao` é o desfecho da aprovação conjunta e `Pedido_Gerado` é o
 * estado seguinte, que a pressupõe. As aprovações parciais (`Aprovado_Pelo_Gestor` /
 * `Aprovado_Pelo_Operador`) NÃO entram: só uma das partes decidiu e o pedido ainda pode mudar.
 */
const STATUS_PEDIDO_APROVADO: FluxoAprovacaoPedidoStatus[] = [
  FluxoAprovacaoPedidoStatus.APROVADO_PARA_GERACAO,
  FluxoAprovacaoPedidoStatus.PEDIDO_GERADO,
];

/**
 * O download do CSV do pedido só é permitido depois que o pedido está aprovado, isto é,
 * após a aprovação conjunta. Antes disso os itens ainda podem ser alterados pela contraparte, e um
 * CSV baixado no meio do fluxo circularia como se fosse a versão final. Reprovado também não libera.
 */
export function podeBaixarCsv(status: FluxoAprovacaoPedidoStatus): boolean {
  return STATUS_PEDIDO_APROVADO.includes(status);
}

/**
 * Deriva os flags de renderizacao da tela do operador a partir do pedido,
 * mantendo o componente enxuto (sem logica de decisao acumulada no JSX).
 * `geracaoLiberada` prioriza o flag da API (PedidoFluxoStatusDTO), caindo para
 * o calculo por status apenas no estado inicial (antes da 1a resposta da API).
 */
export function resolveOperadorView(pedido: FluxoAprovacaoPedidoEntity): OperadorView {
  const status = pedido.status;
  const geracaoLiberada = pedido.geracaoLiberada ?? podeGerarPedido(status);
  const pedidoGerado = status === FluxoAprovacaoPedidoStatus.PEDIDO_GERADO;
  const reprovado = status === FluxoAprovacaoPedidoStatus.REPROVADO;
  const mostrarGerar = !pedidoGerado && !reprovado;
  return {
    podeEnviar: podeEnviarParaAprovacao(status),
    geracaoLiberada,
    mostrarGerar,
    mostrarBloqueio: mostrarGerar && !geracaoLiberada,
    pedidoGerado,
    reprovado,
  };
}

/** Estados em que é a vez do gestor (aprovador da loja) agir sobre a versão atual. */
const STATUS_TURNO_GESTOR: FluxoAprovacaoPedidoStatus[] = [
  FluxoAprovacaoPedidoStatus.AGUARDANDO_APROVACAO_GESTOR,
  FluxoAprovacaoPedidoStatus.APROVADO_PELO_OPERADOR,
];

/** Estados em que é a vez do operador agir sobre a versão retornada. */
const STATUS_TURNO_OPERADOR: FluxoAprovacaoPedidoStatus[] = [
  FluxoAprovacaoPedidoStatus.AGUARDANDO_VALIDACAO_OPERADOR,
  FluxoAprovacaoPedidoStatus.APROVADO_PELO_GESTOR,
];

/** Estados terminais do fluxo (não permitem novas ações). */
const STATUS_TERMINAIS: FluxoAprovacaoPedidoStatus[] = [
  FluxoAprovacaoPedidoStatus.PEDIDO_GERADO,
  FluxoAprovacaoPedidoStatus.REPROVADO,
];

function resolverTurno(status: FluxoAprovacaoPedidoStatus, podeAprovar: boolean): {
  turnoGestor: boolean;
  turnoOperador: boolean;
} {
  return {
    turnoGestor: podeAprovar && STATUS_TURNO_GESTOR.includes(status),
    turnoOperador: !podeAprovar && STATUS_TURNO_OPERADOR.includes(status),
  };
}

function temSolicitacaoPendente(detalhe: PedidoDetalhe): boolean {
  return (detalhe.solicitacoes ?? []).some((s) => s.status === 'PENDENTE');
}

function calcularEmAnalise(status: FluxoAprovacaoPedidoStatus, terminal: boolean, geracaoLiberada: boolean): boolean {
  return !terminal && !geracaoLiberada && !podeEnviarParaAprovacao(status);
}

/**
 * Deriva as ações da visualização do pedido a partir do detalhe. Quem é aprovador da loja
 * (podeAprovar) age como gestor no seu turno (Aprovar/Reprovar/Editar → volta ao operador);
 * os demais agem como operador no seu turno (Validar/Editar → volta ao gestor). Editar só
 * aparece no turno de quem está vendo o pedido; geração só quando liberada (aprovação conjunta).
 */
export function resolveAcoesPedido(detalhe: PedidoDetalhe): AcoesPedidoView {
  const status = detalhe.status;
  // Pedido parado em Aguardando_Ajuste (estado legado, em que reprovar abria ajuste).
  if (status === FluxoAprovacaoPedidoStatus.AGUARDANDO_AJUSTE) {
    return resolveAcoesPedidoAguardandoAjuste(detalhe);
  }
  const podeAprovar = detalhe.podeAprovar === true;
  const geracaoLiberada = detalhe.geracaoLiberada ?? podeGerarPedido(status);
  const terminal = STATUS_TERMINAIS.includes(status);
  const temPendente = temSolicitacaoPendente(detalhe);
  const turno = resolverTurno(status, podeAprovar);
  // O operador só age se tiver vínculo/autoria (podeEditar); o gestor é quem tem vínculo aprovador na loja.
  const turnoGestor = turno.turnoGestor;
  const turnoOperador = turno.turnoOperador && detalhe.podeEditar !== false;
  // Editar só no próprio turno. O aprovador não edita enquanto o pedido aguarda o operador,
  // e o operador não edita enquanto aguarda o gestor. Em elaboração, quem pode agir ainda edita.
  const operadorPodeAgir = !podeAprovar && detalhe.podeEditar !== false;
  const editaEmElaboracao = status === FluxoAprovacaoPedidoStatus.EM_ELABORACAO && (podeAprovar || operadorPodeAgir);
  const mostrarEditar = turnoGestor || turnoOperador || editaEmElaboracao;
  const emAnalise = calcularEmAnalise(status, terminal, geracaoLiberada);
  return {
    mostrarEnviar: podeEnviarParaAprovacao(status) && detalhe.podeEnviar !== false,
    mostrarAprovar: turnoGestor && !temPendente,
    mostrarReprovar: turnoGestor,
    mostrarValidar: turnoOperador && !temPendente,
    mostrarEditar,
    mostrarGerar: geracaoLiberada && !temPendente,
    geracaoLiberada,
    mostrarBloqueio: emAnalise,
    bloqueadoPorPendencia: temPendente && mostrarEditar,
    editarBloqueadoPorPendencia: mostrarEditar && pendenteParaDecidir(detalhe),
    enviarBloqueadoPorPendencia: temPendente || detalhe.pendenciaAprovadorVinculado === true,
    terminal,
  };
}

function pendenteParaDecidir(detalhe: PedidoDetalhe): boolean {
  return (detalhe.solicitacoes ?? []).some((s) => s.status === 'PENDENTE' && s.podeDecidir === true);
}

function resolveAcoesPedidoAguardandoAjuste(detalhe: PedidoDetalhe): AcoesPedidoView {
  const aguardandoAprovacao = detalhe.status === FluxoAprovacaoPedidoStatus.AGUARDANDO_APROVACAO_GESTOR;
  const aguardandoAjuste = detalhe.status === FluxoAprovacaoPedidoStatus.AGUARDANDO_AJUSTE;
  const geracaoLiberada = detalhe.geracaoLiberada ?? podeGerarPedido(detalhe.status);
  const terminal = STATUS_TERMINAIS.includes(detalhe.status);
  return {
    mostrarEnviar: detalhe.podeEnviar === true && podeEnviarParaAprovacao(detalhe.status),
    mostrarAprovar: detalhe.podeAprovar === true && aguardandoAprovacao,
    mostrarReprovar: detalhe.podeAprovar === true && aguardandoAprovacao,
    mostrarValidar: false,
    mostrarEditar: detalhe.podeEditar === true && aguardandoAjuste,
    mostrarGerar: geracaoLiberada,
    geracaoLiberada,
    mostrarBloqueio: !terminal && !geracaoLiberada,
    bloqueadoPorPendencia: false,
    editarBloqueadoPorPendencia: false,
    enviarBloqueadoPorPendencia: false,
    terminal,
  };
}

/** Rotulos das solicitacoes item-a-item (§4.5). */
export const SOLICITACAO_TIPO_LABEL: Record<string, string> = {
  REMOCAO: 'Remoção',
  ADICAO: 'Adição',
  ALTERACAO_QTD: 'Alteração de quantidade',
};

export const SOLICITACAO_STATUS_LABEL: Record<string, string> = {
  PENDENTE: 'Pendente',
  ACEITA: 'Aceita',
  RECUSADA: 'Recusada',
};

/**
 * Rótulos das ações do log de decisões. A API envia o nome do enum
 * (AcaoAuditoriaEnum, ex.: `ENVIO_APROVACAO`); a tela mostra o texto humanizado. Mesma convenção
 * já usada para status e para os tipos de solicitação (label resolvido no front).
 */
export const ACAO_AUDITORIA_LABEL: Record<string, string> = {
  CRIACAO: 'Criação do pedido',
  ENVIO_APROVACAO: 'Envio para aprovação',
  DESTINATARIOS_ELEGIVEIS: 'Aprovadores elegíveis no envio',
  APROVACAO_GESTOR: 'Aprovação do Gestor',
  VALIDACAO_OPERADOR: 'Aprovação do Operador',
  ALTERACAO_GESTOR: 'Alteração do Gestor',
  ALTERACAO_OPERADOR: 'Alteração do Operador',
  RETORNO_OPERADOR: 'Retorno ao Operador',
  REPROVACAO: 'Reprovação',
  REPROVACAO_COM_MARCA: 'Reprovação para ajuste',
  AJUSTE: 'Ajuste pelo comprador',
  NOVA_VERSAO: 'Criação de nova versão',
  REENVIO_APROVACAO: 'Reenvio para aprovação',
  GERACAO: 'Geração do pedido',
  SOLICITACAO_REMOCAO: 'Solicitação de remoção de item',
  SOLICITACAO_ADICAO: 'Solicitação de adição de item',
  SOLICITACAO_ALTERACAO_QTD: 'Solicitação de alteração de quantidade',
  ACEITE_SOLICITACAO: 'Aceite de solicitação',
  RECUSA_SOLICITACAO: 'Recusa de solicitação',
};

/** Ação do log de decisões humanizada; ação desconhecida cai para o valor bruto. */
export function getAcaoLabel(acao?: string): string {
  if (!acao) {
    return '-';
  }
  return ACAO_AUDITORIA_LABEL[acao] ?? acao;
}

/**
 * Humaniza um valor "antes/depois" do log de decisões. Nas ações de transição o
 * valor é um status do pedido (ex.: `Aguardando_Aprovacao_Gestor`) e vira o rótulo legível; nas
 * ações de solicitação é uma quantidade, que passa inalterada por não constar no mapa de status.
 */
export function humanizarValorLog(valor?: string): string | undefined {
  if (!valor) {
    return valor;
  }
  return FLUXO_APROVACAO_PEDIDO_STATUS_LABEL[valor as FluxoAprovacaoPedidoStatus] ?? valor;
}

/**
 * Identificação do item exibida nas telas do fluxo. O usuário reconhece o item pelo código
 * do fabricante — o mesmo "Sufixo" da listagem do Analítico —, não pela PK interna. Cai para o id
 * interno quando o item não tem cadastro correspondente (best-effort, mesma política da descrição),
 * para a linha nunca ficar sem identificação.
 */
export function getCodigoItem(item: { codigo?: string; idItem?: number }): string {
  const codigo = item.codigo?.trim();
  if (codigo) {
    return codigo;
  }
  return item.idItem === undefined || item.idItem === null ? '-' : String(item.idItem);
}

/** Ação escolhida para um item existente no modo de edição (§4.5). */
export type AcaoItemEdicao = 'MANTER' | 'REMOVER' | 'ALTERAR';

/** Linha da grade de edição: item existente + ação escolhida pelo usuário. */
export interface LinhaEdicaoItem {
  idItem: number;
  /** Código (sufixo) do item, só para exibição; a solicitação é montada pelo `idItem`. */
  codigo?: string;
  descricao?: string;
  quantidadeAtual: number;
  acao: AcaoItemEdicao;
  quantidadeNova: number;
}

/** Item novo a ser adicionado via typeahead no modo de edição. */
export interface AdicaoItemEdicao {
  idItem: number;
  /** Código (sufixo) do item, só para exibição; a solicitação é montada pelo `idItem`. */
  codigo?: string;
  quantidade: number;
  valor?: number;
}

/**
 * Monta as solicitações item-a-item (§4.5 / RN12) a partir das ações escolhidas na grade
 * e das adições: REMOVER → REMOCAO; ALTERAR com quantidade diferente → ALTERACAO_QTD; cada adição
 * → ADICAO. Itens "MANTER" (ou "ALTERAR" sem mudança real de quantidade) não geram solicitação.
 */
export function construirSolicitacoes(linhas: LinhaEdicaoItem[], adicoes: AdicaoItemEdicao[]): SolicitacaoItem[] {
  const daGrade = linhas.flatMap((linha) => solicitacaoDaLinha(linha));
  const daAdicao: SolicitacaoItem[] = adicoes
    .filter((a) => a.idItem > 0 && a.quantidade > 0)
    .map((a) => ({ tipo: 'ADICAO', idItem: a.idItem, quantidade: a.quantidade, valor: a.valor }));
  return [...daGrade, ...daAdicao];
}

function solicitacaoDaLinha(linha: LinhaEdicaoItem): SolicitacaoItem[] {
  if (linha.acao === 'REMOVER') {
    return [{ tipo: 'REMOCAO', idItem: linha.idItem }];
  }
  if (linha.acao === 'ALTERAR' && linha.quantidadeNova > 0 && linha.quantidadeNova !== linha.quantidadeAtual) {
    return [{ tipo: 'ALTERACAO_QTD', idItem: linha.idItem, quantidade: linha.quantidadeNova }];
  }
  return [];
}

export function isConflito(statusCode: number): boolean {
  return statusCode === EHttpStatusCode.conflict;
}

/**
 * Indica se uma aprovação/validação bem-sucedida COMPLETOU a aprovação
 * conjunta — isto é, a geração do pedido está liberada (status Aprovado para geração). Quando
 * verdadeiro, a tela encadeia a geração automática do pedido antes de redirecionar para a lista.
 */
export function geracaoLiberadaApos(
  entidade?: { status?: FluxoAprovacaoPedidoStatus; geracaoLiberada?: boolean } | null
): boolean {
  if (!entidade) {
    return false;
  }
  return entidade.geracaoLiberada === true || entidade.status === FluxoAprovacaoPedidoStatus.APROVADO_PARA_GERACAO;
}

/** Converte 'dd/MM/yyyy [HH:mm]' para 'yyyy-MM-dd'; retorna null se não reconhecer o formato. */
function dataCadastroParaIso(dataCadastro: string): string | null {
  const partes = dataCadastro.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (!partes) {
    return null;
  }
  return `${partes[3]}-${partes[2]}-${partes[1]}`;
}

/**
 * Indica se a data de cadastro (formato 'dd/MM/yyyy HH:mm' vindo da API) está dentro do
 * período [inicio, fim] (ambos 'yyyy-MM-dd', vindos dos inputs de data). Limites vazios
 * não restringem; datas em formato desconhecido não são filtradas (não somem da lista).
 */
export function dentroDoPeriodo(dataCadastro: string | undefined, inicio: string, fim: string): boolean {
  if (!dataCadastro) {
    return true;
  }
  const iso = dataCadastroParaIso(dataCadastro);
  if (!iso) {
    return true;
  }
  if (inicio && iso < inicio) {
    return false;
  }
  return !(fim && iso > fim);
}

/** Filtra pedidos do fluxo novo pela data de cadastro dentro do período informado. */
export function filtrarPorPeriodo<T extends { dataCadastro?: string }>(pedidos: T[], inicio: string, fim: string): T[] {
  return pedidos.filter((pedido) => dentroDoPeriodo(pedido.dataCadastro, inicio, fim));
}

/** Quantidade de pedidos por página em cada aba da listagem (Analítico, Sintético e Campanhas). */
export const TAMANHO_PAGINA_LISTAGEM_PEDIDOS = 15;

export function totalPaginasLista(quantidade: number, tamanho = TAMANHO_PAGINA_LISTAGEM_PEDIDOS): number {
  if (quantidade <= 0) {
    return 1;
  }
  return Math.ceil(quantidade / tamanho);
}

export function fatiaPagina<T>(itens: T[], pagina: number, tamanho = TAMANHO_PAGINA_LISTAGEM_PEDIDOS): T[] {
  const indice = Math.max(0, pagina) * tamanho;
  return itens.slice(indice, indice + tamanho);
}

/** Pedido de Campanhas: a grade segue as colunas de preço e totalizador do relatório. */
export function exibirColunasCampanha(origem?: string): boolean {
  return origem === 'CAMPANHAS';
}

export function linhaTemPrecoCampanha(item: {
  precoSemSt?: number | null;
  precoSemStDesconto?: number | null;
  precoComSt?: number | null;
}): boolean {
  return item.precoSemSt != null || item.precoSemStDesconto != null || item.precoComSt != null;
}

export function listaTemPrecoCampanha(
  itens: { precoSemSt?: number | null; precoSemStDesconto?: number | null; precoComSt?: number | null }[]
): boolean {
  return itens.some(linhaTemPrecoCampanha);
}

/**
 * Resolve a mensagem a exibir ao operador a partir do retorno da API,
 * priorizando a mensagem/erro da propria API e caindo para um texto amigavel.
 */
export function resolverMensagemErro(
  result: ServiceOperationResult<unknown> | null | undefined,
  fallback: string
): string {
  if (result?.message && result.message.trim() !== '' && result.message !== MENSAGEM_API_GENERICA) {
    return result.message;
  }
  if (result?.errors && result.errors.length > 0 && result.errors[0].trim() !== '') {
    return result.errors[0];
  }
  return fallback;
}

export const MENSAGEM_ORIGINAR_FALLBACK = 'Não foi possível solicitar a aprovação do pedido.';

export interface RespostaOriginar {
  statusCode: number;
  headers: Record<string, never>;
  body: ServiceOperationResult<unknown> | null;
  alertaExibido?: boolean;
}

/** Sucesso navega; falha exibe a mensagem da API (nunca um texto genérico no lugar dela). */
export function finalizarOriginar(
  resultado: ServiceOperationResult<unknown> | null | undefined,
  onSucesso: () => void,
  exibirErro: (mensagem: string, statusCode?: number) => void
): RespostaOriginar {
  if (resultado?.success === true) {
    onSucesso();
    return { statusCode: 200, headers: {}, body: resultado };
  }
  exibirErro(resolverMensagemErro(resultado, MENSAGEM_ORIGINAR_FALLBACK), resultado?.statusCode);
  return { statusCode: 500, headers: {}, body: resultado ?? null, alertaExibido: true };
}

let mapaFiliaisCache: Map<number, string> | null = null;

export function registrarFiliaisNoCache(
  filiais: Array<{ id?: number | string; idFilial?: number | string; nome?: string; nomeFilial?: string; descricao?: string }>
): void {
  if (!mapaFiliaisCache) {
    mapaFiliaisCache = new Map();
  }
  for (const f of filiais) {
    const id = Number(f.id ?? f.idFilial);
    const nome = f.nome ?? f.nomeFilial ?? f.descricao;
    if (id && nome) {
      mapaFiliaisCache.set(id, nome.trim());
    }
  }
}

export async function carregarFiliaisParaCache(): Promise<Map<number, string>> {
  if (mapaFiliaisCache && mapaFiliaisCache.size > 0) {
    return mapaFiliaisCache;
  }
  const endpoint = getEnv('REACT_APP_END_POINT_FILIAL') || getEnv('REACT_APP_ENDPOINT_FILIAL') || '/api/filial';
  try {
    const response = await new Filial(`${endpoint}?modulos=COMPRAS`).Get();
    if (response && response.statusCode !== 500 && Array.isArray(response.body)) {
      registrarFiliaisNoCache(response.body);
    }
  } catch {
    // best-effort
  }
  return mapaFiliaisCache ?? new Map();
}

export function obterNomeFilialCache(idFilial?: number | string): string | undefined {
  if (idFilial === undefined || idFilial === null || !mapaFiliaisCache) {
    return undefined;
  }
  return mapaFiliaisCache.get(Number(idFilial));
}

export function formatarCombinacao(detalhe: PedidoDetalhe, nomeFilialOverride?: string): string {
  const nomeEmpresaStorage = (() => {
    try {
      const userInfoCoded = localStorage.getItem(getEnv('REACT_APP_LOCALSTORAGE_INFO_CHECK'));
      if (userInfoCoded) {
        const decoded = window.atob(userInfoCoded);
        const parsed = JSON.parse(decoded);
        return parsed?.nomeEmpresa;
      }
    } catch {
      return undefined;
    }
    return undefined;
  })();

  const empresa = (detalhe.nomeEmpresa || nomeEmpresaStorage || (detalhe.idEmpresa ? `Empresa ${detalhe.idEmpresa}` : '')).toUpperCase().trim();
  const nomeFilialResolvido = nomeFilialOverride || detalhe.nomeFilial || obterNomeFilialCache(detalhe.idFilial);
  const filial = (nomeFilialResolvido || (detalhe.idFilial ? `Filial ${detalhe.idFilial}` : '')).toUpperCase().trim();

  const partes: string[] = [];
  if (empresa) {
    partes.push(`EMPRESA: ${empresa}`);
  }
  if (filial) {
    partes.push(`FILIAIS: ${filial}`);
  }

  if (partes.length === 0) {
    return '';
  }

  return `COMBINAÇÃO: ${partes.join(' / ')}`;
}

